import { positiveField, without } from "../fields";
import { isProductionTick } from "../run/cadence";
import { addSupply, supplyOf } from "../run/pools";
import { seatAt, soldierSeats, withSeat } from "../run/seats";
import type { RunRules } from "../types/rules";
import type { RunState, SeatId } from "../types/state";
import { owedOf, purseOf, renownOf } from "./prospect";
import { earnValor, valorRulesOf } from "./valor";

/**
 * The kingdom pays its soldier seats (C1): the deeds, their bounty and renown, and the debts
 * settled when the pool refills. Every soldier seat is paid alike, held or not (K0).
 */

type DeedKind =
  | "land"
  | "banner"
  | "squad"
  | "squadBanner"
  | "sale"
  | "steadied"
  | "kill"
  | "together"
  | "menKill"
  | "menTogether"
  | "soloSale"
  | "slay"
  | "veteranSlay"
  | "broke";

/**
 * V1: the deeds that earn valor and no renown or pay of the King's, so a run without the valor
 * block (and, for a kill by its blow, without VC's head money) never pays them: a load sold alone,
 * a kill by its blow, an enemy veteran's, a foe broken by its blow.
 */
const VALOR_ONLY: ReadonlySet<DeedKind> = new Set(["soloSale", "slay", "veteranSlay", "broke"]);

/** What a deed pays from the pool and earns in renown: the King's pay (C1) and the deeds of company (C2). */
function deedTerms(rules: RunRules, kind: DeedKind): { bounty: number; renown: number } {
  const { bounty, company } = rules;
  switch (kind) {
    case "land":
      return { bounty: bounty?.landBounty ?? 0, renown: bounty?.landRenown ?? 0 };
    case "banner":
      return { bounty: bounty?.bannerBounty ?? 0, renown: bounty?.bannerRenown ?? 0 };
    // The squad's deed was the squad's: its pay is shared, its renown is not.
    case "squad":
      return { bounty: bounty?.landBounty ?? 0, renown: 0 };
    case "squadBanner":
      return { bounty: bounty?.bannerBounty ?? 0, renown: 0 };
    case "sale":
      return { bounty: 0, renown: company?.saleRenown ?? 0 };
    case "steadied":
      return { bounty: 0, renown: company?.steadyRenown ?? 0 };
    // A kill struck together (C2), the human's blow last ("kill") or a comrade's ("together", V1).
    case "kill":
    case "together":
      return { bounty: 0, renown: company?.killRenown ?? 0 };
    // VC: a kill struck together with its own men; the deed was the squad's, so no renown.
    case "menKill":
    case "menTogether":
      return { bounty: 0, renown: 0 };
    case "soloSale":
    case "slay":
    case "veteranSlay":
    case "broke":
      return { bounty: 0, renown: 0 };
  }
}

/** What a deed earns in valor (V1, section 5.1 of the sketch): a load sold its `worth`; 0 without the block. */
function valorTerm(rules: RunRules, kind: DeedKind, worth: number): number {
  const valor = valorRulesOf(rules);
  if (!valor) {
    return 0;
  }
  switch (kind) {
    case "sale":
    case "soloSale":
      return worth;
    case "kill":
    case "menKill":
      return 0;
    case "menTogether":
      return valor.deeds.together;
    default:
      return valor.deeds[kind];
  }
}

/** VC: the head money a kill by its blow is paid, an enemy veteran's more; 0 without the coins block. */
function headTerm(rules: RunRules, kind: DeedKind): number {
  const coins = rules.soldier?.coins;
  if (!coins) {
    return 0;
  }
  if (kind === "slay") {
    return coins.killBounty;
  }
  return kind === "veteranSlay" ? coins.veteranBounty : 0;
}

/**
 * The kingdom pays a soldier seat for a deed (C1). Its pool pays what it holds and never
 * goes under 0; the rest is owed, up to `debtCap`; the renown is earned either way. Deeds of
 * one tick add up in the seat's `deed`. A deed of company (C2) is counted the same way; `bonus` is
 * what the company added to a sale, paid by the market and not by the pool. With the valor block
 * (V1) the deed earns its valor too, `worth` for a load sold, and with the coins block (VC) a kill
 * by its blow earns head money, paid and owed in fields of its own; each caller pays a deed only while
 * the soldier is at work (A62): each new kind's caller checks it, and a sale's worth is 0 off work.
 */
export function payDeed(
  state: RunState,
  seatId: SeatId,
  deed: DeedKind,
  bonus = 0,
  worth = 0,
): RunState {
  const rules = state.rules.bounty;
  const seat = seatAt(state, seatId);
  const valor = valorTerm(state.rules, deed, worth);
  const head = headTerm(state.rules, deed);
  if (!rules || !seat || (VALOR_ONLY.has(deed) && valor === 0 && head === 0)) {
    return state;
  }
  const { bounty, renown } = deedTerms(state.rules, deed);
  const pool = supplyOf(state, seat.faction);
  const paid = Math.min(bounty, pool);
  const debt = owedOf(seat);
  // VC: head money's debt is its own, under the same cap as the King's pay.
  const headDebt = seat.headMoneyOwed ?? 0;
  const owed = Math.min(bounty - paid, Math.max(0, rules.debtCap - debt - headDebt));
  const headPaid = Math.min(head, pool - paid);
  const headOwed = Math.min(head - headPaid, Math.max(0, rules.debtCap - debt - owed - headDebt));
  const sameTick = seat.deed?.tick === state.tick ? seat.deed : undefined;
  const one = (kind: DeedKind): number => (deed === kind ? 1 : 0);
  const paidState = withSeat(addSupply(state, seat.faction, -(paid + headPaid)), {
    ...seat,
    ...(headOwed > 0 ? { headMoneyOwed: headDebt + headOwed } : {}),
    purse: purseOf(seat) + paid + headPaid,
    renown: renownOf(seat) + renown,
    owed: debt + owed,
    deed: {
      tick: state.tick,
      land: (sameTick?.land ?? 0) + one("land"),
      banners: (sameTick?.banners ?? 0) + one("banner"),
      paid: (sameTick?.paid ?? 0) + paid,
      owed: (sameTick?.owed ?? 0) + owed,
      renown: (sameTick?.renown ?? 0) + renown,
      ...positiveField("squad", (sameTick?.squad ?? 0) + one("squad")),
      ...positiveField("sales", (sameTick?.sales ?? 0) + one("sale")),
      ...positiveField("bonus", (sameTick?.bonus ?? 0) + bonus),
      ...positiveField("steadied", (sameTick?.steadied ?? 0) + one("steadied")),
      ...positiveField(
        "kills",
        (sameTick?.kills ?? 0) +
          one("kill") +
          one("together") +
          one("menKill") +
          one("menTogether"),
      ),
      ...positiveField("squadBanners", (sameTick?.squadBanners ?? 0) + one("squadBanner")),
      ...positiveField("slain", (sameTick?.slain ?? 0) + one("slay")),
      ...positiveField("veteransSlain", (sameTick?.veteransSlain ?? 0) + one("veteranSlay")),
      ...positiveField("routed", (sameTick?.routed ?? 0) + one("broke")),
      ...positiveField("valor", (sameTick?.valor ?? 0) + valor),
      ...positiveField("headMoneyPaid", (sameTick?.headMoneyPaid ?? 0) + headPaid),
      ...positiveField("headMoneyOwed", (sameTick?.headMoneyOwed ?? 0) + headOwed),
    },
  });
  return earnValor(paidState, seat.id, valor);
}

/** `count` deeds of one kind, paid in turn: each from the pool the one before it left (`payDeed`). */
export function payDeeds(state: RunState, seatId: SeatId, deed: DeedKind, count: number): RunState {
  let paid = state;
  for (let done = 0; done < count; done += 1) {
    paid = payDeed(paid, seatId, deed);
  }
  return paid;
}

/**
 * Step 1, after the income (C1), seat by seat in id order: the pool pays down what the King owes
 * the seat, his pay first, then the head money (VC) with what is left.
 */
export function settleDebts(state: RunState): RunState {
  if (!state.rules.bounty || !isProductionTick(state)) {
    return state;
  }
  let next = state;
  for (const { id } of soldierSeats(state)) {
    next = settleSeat(next, id);
  }
  return next;
}

function settleSeat(state: RunState, seatId: SeatId): RunState {
  const seat = seatAt(state, seatId);
  if (!seat) {
    return state;
  }
  const pool = supplyOf(state, seat.faction);
  const paid = Math.min(owedOf(seat), pool);
  const headDebt = seat.headMoneyOwed ?? 0;
  const head = Math.min(headDebt, pool - paid);
  if (paid === 0 && head === 0) {
    return state;
  }
  const settled = { ...seat, purse: purseOf(seat) + paid + head, owed: owedOf(seat) - paid };
  return withSeat(
    addSupply(state, seat.faction, -(paid + head)),
    head === 0
      ? settled
      : headDebt - head > 0
        ? { ...settled, headMoneyOwed: headDebt - head }
        : without(settled, "headMoneyOwed"),
  );
}
